'use client';

import {
  BADGE_STATUS,
  Badge,
  BadgeSeverity,
  BadgeStatus,
  BodyStrong,
  MetaDefault,
  MonoSmall,
  Tooltip,
  getStatusSeverity,
  type BadgeStatusType,
} from '@terra/ui-design-system';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import { useTranslations } from 'next-intl';

type DirectoryScanResult = {
  url: string;
  statusCode: number;
  contentLength?: number | null;
  verificationStatus: 'PENDING' | 'VERIFIED' | 'SOFT_404' | 'FAILED';
  candidateId?: string | null;
};

const VERIFICATION_BADGE_BY_STATUS: Record<
  DirectoryScanResult['verificationStatus'],
  BadgeStatusType
> = {
  PENDING: BADGE_STATUS.NEW,
  VERIFIED: BADGE_STATUS.RESOLVED,
  SOFT_404: BADGE_STATUS.RETEST,
  FAILED: BADGE_STATUS.UNRESOLVED,
};

function getDisplayPath(url: string): string {
  try {
    const parsed = new URL(url);

    return `${parsed.pathname}${parsed.search}`;
  } catch {
    return url;
  }
}

function formatBytes(bytes: number): string {
  if (bytes < 1000) {
    return `${bytes}B`;
  }

  if (bytes < 1_000_000) {
    return `${(bytes / 1000).toFixed(bytes >= 10_000 ? 0 : 1).replace(/\.0$/, '')}KB`;
  }

  return `${(bytes / 1_000_000).toFixed(1).replace(/\.0$/, '')}MB`;
}

export function DirectoryScanResults({
  results,
}: {
  results: DirectoryScanResult[];
}) {
  const t = useTranslations('directoryScan');
  const verificationLabels = {
    PENDING: t('results.pending'),
    VERIFIED: t('results.verified'),
    SOFT_404: t('results.soft404'),
    FAILED: t('results.failed'),
  };

  return (
    <VStack gap="2" w="full">
      <HStack gap="2" alignItems="center">
        <BodyStrong>{t('results.title')}</BodyStrong>
        <Badge variant="outline" size="sm">
          {results.length}
        </Badge>
      </HStack>

      <VStack
        gap="0"
        w="full"
        maxH="280px"
        overflowY="auto"
        borderWidth="1px"
        borderColor="border.default"
        borderRadius="md"
      >
        {results.map((result, index) => {
          const path = getDisplayPath(result.url);
          const statusSeverity = getStatusSeverity(result.statusCode);
          const isLast = index === results.length - 1;

          return (
            <HStack
              key={`${result.url}-${result.candidateId ?? 'none'}`}
              w="full"
              alignItems="center"
              gap="2"
              px="2"
              py="1"
              minH="30px"
              borderBottomWidth={isLast ? '0' : '1px'}
              borderColor="border.muted"
            >
              {statusSeverity ? (
                <BadgeSeverity severity={statusSeverity} size="sm">
                  {result.statusCode}
                </BadgeSeverity>
              ) : (
                <Badge size="sm" variant="emphasis">
                  {result.statusCode}
                </Badge>
              )}

              <Tooltip title={result.url} positioning={{ strategy: 'fixed' }}>
                <MonoSmall color="fg.default" truncate flex="1" minW={0}>
                  {path}
                </MonoSmall>
              </Tooltip>

              {typeof result.contentLength === 'number' ? (
                <MonoSmall
                  color="fg.muted"
                  flexShrink={0}
                  minW="48px"
                  textAlign="right"
                >
                  {formatBytes(result.contentLength)}
                </MonoSmall>
              ) : (
                <Box minW="48px" flexShrink={0} />
              )}

              <Box flexShrink={0}>
                <BadgeStatus
                  status={
                    VERIFICATION_BADGE_BY_STATUS[result.verificationStatus]
                  }
                  size="sm"
                >
                  {verificationLabels[result.verificationStatus]}
                </BadgeStatus>
              </Box>
            </HStack>
          );
        })}
      </VStack>

      {results.length === 0 ? (
        <MetaDefault color="fg.muted">{t('results.empty')}</MetaDefault>
      ) : null}
    </VStack>
  );
}
